import * as React from 'react';
import classnames from 'classnames';
import { isEmpty, isNil } from 'lodash';

import ActionButtonDropdownMenuItem from '../item/ActionButtonDropdownMenuItem';

import { __t, applyTranslations } from 'utils/language';
import { getElementDetails } from '../../utils/action-button-dropdown';

import './action-button-dropdown-menu.scss';

import type {
  ActionButtonDropdownElementInfo,
  ActionButtonDropdownElementRef,
  ActionButtonDropdownItem,
  ActionButtonDropdownStyle
} from 'types';

type Props = {
  items: Array<ActionButtonDropdownItem>,
  style?: ActionButtonDropdownStyle,
  controlRef: ActionButtonDropdownElementRef<HTMLElement>,
  withSelectable?: boolean,
  withCenteredMenu?: boolean,
  withRightAlignedMenu?: boolean,
  withCustomWidthMenu?: boolean,
  hasCustomButton: boolean,
  CustomMenu?: React.ReactNode,
  menuClassName?: string,
  menuItemClassName?: string,
  onItemClick: (identifier: string) => void
};

type State = {
  top: number,
  left: number,
  width: number | string
};

applyTranslations(require('./action-button-dropdown-menu.i18n.json'));

const MENU_DISTANCE_FROM_CONTROL = 4;
const msgKey = 'action_button_dropdown_menu';

class ActionButtonDropdownMenu extends React.PureComponent<Props, State> {
  state = {
    top: 0,
    left: 0,
    width: 0
  };

  menuRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    this.setPlacementStyles();
  }

  componentDidUpdate() {
    this.setPlacementStyles();
  }

  setPlacementStyles(): void {
    const { controlRef, hasCustomButton } = this.props;
    const controlDetails = getElementDetails(controlRef.current);

    if (!controlDetails) {
      return;
    }

    this.setState({
      top: controlDetails.bottom + MENU_DISTANCE_FROM_CONTROL,
      left: controlDetails.left,
      width: hasCustomButton ? 'auto' : controlDetails.width
    }, () => this.setCustomPlacementStyles(controlDetails));
  }

  setCustomPlacementStyles(controlDetails: Partial<ActionButtonDropdownElementInfo>): void {
    const { hasCustomButton, withCenteredMenu, withRightAlignedMenu } = this.props;
    const menuDetails = getElementDetails(this.menuRef.current);

    if (hasCustomButton && withCenteredMenu) {
      if (!menuDetails || isNil(controlDetails.left) || isNil(controlDetails.width)) {
        // can't calculate proper placement
        return;
      }

      const menuWidth = menuDetails.right - menuDetails.left;
      const controlWidth = controlDetails.width;

      this.setState({
        left: controlDetails.left - ((menuWidth - controlWidth) / 2)
      });
    }

    if (withRightAlignedMenu) {
      if (!menuDetails || isNil(controlDetails.right)) {
        // can't calculate proper placement
        return;
      }

      const menuWidth = menuDetails.right - menuDetails.left;

      this.setState({
        left: controlDetails.right - menuWidth
      });
    }
  }

  hasCustomMenu(): boolean {
    const { CustomMenu } = this.props;

    return !isNil(CustomMenu);
  }

  renderEmpty(): React.ReactNode {
    return (
      <div className="action-button-dropdown-menu__empty">
        {__t(`${msgKey}.messages.empty`)}
      </div>
    );
  }

  renderItem = (item: ActionButtonDropdownItem): JSX.Element => {
    const { style, withSelectable, menuItemClassName, onItemClick } = this.props;

    return (
      <ActionButtonDropdownMenuItem
        key={`action-button-dropdown-menu__item-${item.identifier}`}
        item={item}
        style={style}
        withSelectable={withSelectable}
        menuItemClassName={menuItemClassName}
        onItemClick={onItemClick}
      />
    );
  };

  renderItems(): React.ReactNode {
    const { items } = this.props;

    return (
      <div className="action-button-dropdown-menu__items">
        {
          items.map(this.renderItem)
        }
      </div>
    );
  }

  renderCustomMenu(): React.ReactNode {
    const { CustomMenu } = this.props;

    return (
      <div className="action-button-dropdown-menu__custom">
        {CustomMenu}
      </div>
    );
  }

  renderMenu(): React.ReactNode {
    return this.hasCustomMenu()
      ? this.renderCustomMenu()
      : this.renderItems();
  }

  render(): React.ReactNode {
    const { items, menuClassName, CustomMenu, withCustomWidthMenu } = this.props;
    const { top, left, width } = this.state;

    const menuClasses = classnames('action-button-dropdown-menu', menuClassName, {
      'action-button-dropdown-menu--non-custom': !this.hasCustomMenu()
    });

    const newWidth = withCustomWidthMenu ? 'auto' : width;

    return (
      <div
        ref={this.menuRef}
        className={menuClasses}
        style={{ top: top + window.scrollY, left, width: newWidth }}
      >
        {
          isEmpty(items) && isNil(CustomMenu)
            ? this.renderEmpty()
            : this.renderMenu()
        }
      </div>
    );
  }
}

export default ActionButtonDropdownMenu;
